import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import moment from 'moment';
import { SalesComparisonWidget, WeeklySalesPoint } from '../../models/loyalty-dashboard.model';

interface ChartRow {
  category: string;
  merchantDollars: number;
  peerDollars: number | null;
}

interface BoxStyle {
  left: string;
  width: string;
}

interface ChartLayout {
  labelBefore: BoxStyle;
  labelAfter: BoxStyle;
  shade: BoxStyle;
  separator: BoxStyle;
}

/**
 * The plotted width is divided into three independently-sized sections: before-loyalty, a fixed
 * visual gap, and after-loyalty. The gap is NOT derived from how many data points are on either
 * side (that was the earlier bug — it made the gap's apparent size drift with the before/after
 * point counts, and tied the separator bar's edges to a category-index midpoint that didn't line
 * up pixel-for-pixel with the HTML overlay). Instead the x-axis itself is a plain 0..1 value axis
 * (see buildChartOptions) and every point — plus the shading/separator/labels — is positioned from
 * these same three fractions, so they can never disagree.
 */
const GAP_FRACTION = 0.015;

/** Must match buildChartOptions()'s `grid` config below — the HTML overlays (period labels, separator) are positioned to match the plotted area exactly. */
const GRID_LEFT_PX = 90;
const GRID_RIGHT_PX = 24;
const GRID_TOP_PX = 10;
const GRID_BOTTOM_PX = 64;

interface Sections {
  hasBefore: boolean;
  /** Fraction (of the plotted width) the before-loyalty section occupies, starting at 0. */
  beforeWidth: number;
  /** Fraction where the after-loyalty section starts (i.e. beforeWidth + the gap, when there is one). */
  gapEnd: number;
  /** Fraction the after-loyalty section occupies, ending at 1. */
  afterWidth: number;
}

@Component({
  selector: 'app-sales-comparison-widget',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  template: `
    <div class="sales-comparison-card">
      <div class="sales-comparison-card__titlebar">
        <div class="sales-comparison-card__heading">
          <h3 class="sales-comparison-card__title">{{ title }}</h3>
        </div>
        <div class="sales-comparison__legend" *ngIf="data as widget">
          <ng-container *ngIf="sinceWeekCount >= 4">
            <span class="sales-comparison__legend-item">
              <span class="sales-comparison__swatch sales-comparison__swatch--you"></span>
              You
            </span>
            <span class="sales-comparison__legend-item">
              <span class="sales-comparison__swatch sales-comparison__swatch--peer"></span>
              {{ widget.peerGroup.name }}
            </span>
            <span class="sales-comparison__legend-item" *ngIf="hasTrialBand">
              <span class="sales-comparison__swatch sales-comparison__swatch--trial"></span>
              Free trial
            </span>
          </ng-container>
        </div>
      </div>

      <div class="sales-comparison-card__body">
        <div class="sales-comparison" *ngIf="data as widget; else noData">
          <p class="sales-comparison__empty" *ngIf="sinceWeekCount < 4; else chartBlock">
            Your comparison will appear after 4 full weeks with us. {{ sinceWeekCount }} of 4 weeks so far.
          </p>
          <ng-template #chartBlock>
            <div class="sales-comparison__chart-group">
              <div class="sales-comparison__periods" *ngIf="layout as l">
                <span
                  class="sales-comparison__period"
                  [style.left]="l.labelBefore.left"
                  [style.width]="l.labelBefore.width"
                  >Before loyalty</span
                >
                <span
                  class="sales-comparison__period"
                  [style.left]="l.labelAfter.left"
                  [style.width]="l.labelAfter.width"
                  >Current (with loyalty)</span
                >
              </div>

              <div class="sales-comparison__chart-wrap">
                <div
                  class="sales-comparison__before-shade"
                  *ngIf="layout as l"
                  [style.left]="l.shade.left"
                  [style.width]="l.shade.width"
                ></div>
                <div
                  echarts
                  class="sales-comparison__chart"
                  [options]="chartOptions"
                  [attr.aria-label]="ariaSummary"
                  role="img"
                ></div>
                <div
                  class="sales-comparison__separator"
                  *ngIf="layout as l"
                  [style.left]="l.separator.left"
                  [style.width]="l.separator.width"
                ></div>
              </div>
            </div>

            <table class="visually-hidden">
              <caption>
                Weekly revenue, you vs.
                {{
                  widget.peerGroup.name
                }}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Week of</th>
                  <th scope="col">Your weekly revenue</th>
                  <th scope="col">Peer-equivalent weekly revenue</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of rows">
                  <td>{{ row.category }}</td>
                  <td>\${{ row.merchantDollars.toLocaleString() }}</td>
                  <td>{{ row.peerDollars != null ? '$' + row.peerDollars.toLocaleString() : 'No data' }}</td>
                </tr>
              </tbody>
            </table>
          </ng-template>
        </div>
        <ng-template #noData>
          <p class="sales-comparison__empty">No data available.</p>
        </ng-template>
      </div>
    </div>
  `,
  styles: [
    `
      .sales-comparison-card {
        background: #ffffff;
        border: 1px solid #dee2e6;
        border-radius: 3px;
        color: #495057;
      }
      .sales-comparison-card__titlebar {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.75rem;
        padding: 1rem 1rem 0.5rem;
      }
      .sales-comparison-card__heading {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
      }
      .sales-comparison-card__title {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
      }
      .sales-comparison-card__body {
        padding: 0 1rem 1rem;
      }
      .sales-comparison {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .sales-comparison__empty {
        opacity: 0.7;
      }
      .sales-comparison__legend {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        font-size: 0.85rem;
      }
      .sales-comparison__legend-item {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
      }
      .sales-comparison__swatch {
        display: inline-block;
        width: 1.1rem;
        height: 0;
        border-top-width: 3px;
        border-top-style: solid;
      }
      .sales-comparison__swatch--you {
        border-top-color: #1f5fa8;
      }
      .sales-comparison__swatch--peer {
        border-top-style: dashed;
        border-top-color: #b85a1c;
      }
      .sales-comparison__swatch--trial {
        border-top: none;
        background: #f2f6fb;
        height: 0.7rem;
      }
      .sales-comparison__chart-group {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .sales-comparison__chart-wrap {
        position: relative;
      }
      .sales-comparison__chart {
        width: 100%;
        height: 320px;
        position: relative;
        z-index: 1;
      }
      .sales-comparison__before-shade {
        position: absolute;
        top: 10px;
        bottom: 64px;
        background: rgba(138, 143, 150, 0.1);
        pointer-events: none;
      }
      .sales-comparison__separator {
        position: absolute;
        top: 10px;
        bottom: 64px;
        background: #4b5563;
        pointer-events: none;
        z-index: 2;
      }
      .sales-comparison__periods {
        position: relative;
        height: 1rem;
        font-size: 0.8rem;
        line-height: 1;
        opacity: 0.7;
      }
      .sales-comparison__period {
        position: absolute;
        top: 0;
        text-align: center;
        white-space: nowrap;
      }
    `,
  ],
})
export class SalesComparisonWidgetComponent implements OnChanges {
  @Input() data: SalesComparisonWidget | null = null;

  protected rows: ChartRow[] = [];
  protected chartOptions: EChartsOption | null = null;
  protected sinceWeekCount = 0;
  protected beforeWeekCount = 0;
  protected hasBeforePeriod = false;
  protected hasTrialBand = false;
  protected title = 'Your sales vs. similar businesses';
  protected ariaSummary = '';
  protected layout: ChartLayout | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if ('data' in changes) {
      this.rebuild();
    }
  }

  private rebuild(): void {
    const widget = this.data;
    const rawBeforeWeeks = widget?.periods.find((p) => p.kind === 'before')?.weeks ?? [];
    const sinceWeeks = widget?.periods.find((p) => p.kind === 'since')?.weeks ?? [];

    this.hasBeforePeriod = rawBeforeWeeks.length > 0;
    this.sinceWeekCount = sinceWeeks.length;

    if (!widget || this.sinceWeekCount < 4) {
      this.chartOptions = null;
      this.rows = [];
      this.beforeWeekCount = 0;
      this.layout = null;
      return;
    }

    // Keep the before-loyalty section to at most ~25% of the plotted points, merging extra
    // weeks together rather than letting it dominate the graph.
    const maxBeforePoints = Math.max(1, Math.ceil(this.sinceWeekCount / 3));
    const beforeWeeks = this.aggregateWeeks(rawBeforeWeeks, maxBeforePoints);
    this.beforeWeekCount = beforeWeeks.length;

    this.rows = this.buildRows(beforeWeeks, sinceWeeks);
    this.hasTrialBand = !!widget.trialEndDate;
    this.ariaSummary = `Line chart comparing your weekly revenue to ${widget.peerGroup.name}, from ${
      this.rows[0]?.category
    } to ${this.rows[this.rows.length - 1]?.category}.`;

    const sections = this.computeSections();
    this.layout = this.buildLayout(sections);
    this.chartOptions = this.buildChartOptions(widget, sections);
  }

  /** The three section widths (before / gap / after), as fractions of the plotted width. See GAP_FRACTION. */
  private computeSections(): Sections {
    const hasBefore = this.beforeWeekCount > 0;
    if (!hasBefore) {
      return { hasBefore, beforeWidth: 0, gapEnd: 0, afterWidth: 1 };
    }
    const total = this.beforeWeekCount + this.sinceWeekCount;
    const beforeWidth = (1 - GAP_FRACTION) * (this.beforeWeekCount / total);
    const gapEnd = beforeWidth + GAP_FRACTION;
    return { hasBefore, beforeWidth, gapEnd, afterWidth: 1 - gapEnd };
  }

  /** Evenly spaces each side's points across its own section width (see computeSections()). */
  private computeXPositions(sections: Sections): { beforeX: number[]; sinceX: number[] } {
    const b = this.beforeWeekCount;
    const s = this.sinceWeekCount;
    const beforeX = Array.from({ length: b }, (_, i) =>
      b > 1 ? (i / (b - 1)) * sections.beforeWidth : sections.beforeWidth / 2
    );
    const sinceX = Array.from({ length: s }, (_, j) =>
      s > 1 ? sections.gapEnd + (j / (s - 1)) * sections.afterWidth : sections.gapEnd
    );
    return { beforeX, sinceX };
  }

  /**
   * Boxes for the before/after shading, the separator bar, and the period labels, expressed as CSS
   * `calc()` strings against the plotted area (inset by GRID_*_PX). The shade box intentionally
   * extends all the way through the gap to `gapEnd` (not just to `beforeWidth`) and the separator is
   * layered on top via z-index — so even if the two elements' rounded pixel edges don't land on the
   * exact same device pixel, there's no seam, since the shade is already there underneath.
   */
  private buildLayout(sections: Sections): ChartLayout | null {
    if (!sections.hasBefore) {
      return null;
    }
    const insets = GRID_LEFT_PX + GRID_RIGHT_PX;
    const innerWidth = `(100% - ${insets}px)`;
    const box = (leftFraction: number, widthFraction: number): BoxStyle => ({
      left: `calc(${GRID_LEFT_PX}px + ${leftFraction} * ${innerWidth})`,
      width: `calc(${widthFraction} * ${innerWidth})`,
    });

    // The separator fills the entire gap (not a thinner bar centered within a wider gap) so it's
    // flush against both the before-loyalty shading and the first after-loyalty point, with no
    // visible space on either side. To make the bar itself look thinner, shrink GAP_FRACTION.
    return {
      labelBefore: box(0, sections.beforeWidth),
      labelAfter: box(sections.gapEnd, sections.afterWidth),
      shade: box(0, sections.beforeWidth),
      separator: box(sections.beforeWidth, GAP_FRACTION),
    };
  }

  /** Merges `weeks` down to at most `maxPoints` points by averaging consecutive groups. */
  private aggregateWeeks(weeks: WeeklySalesPoint[], maxPoints: number): WeeklySalesPoint[] {
    if (weeks.length <= maxPoints) {
      return weeks;
    }
    const bucketSize = Math.ceil(weeks.length / maxPoints);
    const buckets: WeeklySalesPoint[] = [];
    for (let i = 0; i < weeks.length; i += bucketSize) {
      const slice = weeks.slice(i, i + bucketSize);
      const merchantRevenue = slice.reduce((sum, w) => sum + w.merchantRevenue, 0) / slice.length;
      const peerValues = slice.map((w) => w.peerAverageRevenue).filter((value): value is number => value != null);
      const peerAverageRevenue =
        peerValues.length > 0 ? peerValues.reduce((sum, v) => sum + v, 0) / peerValues.length : null;
      buckets.push({ weekStart: slice[0].weekStart, merchantRevenue, peerAverageRevenue });
    }
    return buckets;
  }

  private buildRows(beforeWeeks: WeeklySalesPoint[], sinceWeeks: WeeklySalesPoint[]): ChartRow[] {
    return [...beforeWeeks, ...sinceWeeks].map((week) => ({
      category: week.weekStart,
      merchantDollars: Math.round(week.merchantRevenue),
      peerDollars: week.peerAverageRevenue != null ? Math.round(week.peerAverageRevenue) : null,
    }));
  }

  private buildChartOptions(widget: SalesComparisonWidget, sections: Sections): EChartsOption {
    const beforeRows = this.rows.slice(0, this.beforeWeekCount);
    const sinceRows = this.rows.slice(this.beforeWeekCount);
    const { beforeX, sinceX } = this.computeXPositions(sections);

    const ticks = this.buildAxisTicks(beforeRows, sinceRows, beforeX, sinceX);
    const markArea = this.buildMarkArea(widget, sinceRows, sinceX);
    const tickValues = ticks.map((t) => t.x);

    return {
      animation: false,
      grid: { left: GRID_LEFT_PX, right: GRID_RIGHT_PX, top: GRID_TOP_PX, bottom: GRID_BOTTOM_PX },
      xAxis: {
        type: 'value',
        min: 0,
        max: 1,
        splitLine: { show: false },
        axisLabel: {
          customValues: tickValues,
          formatter: (value: number) => ticks.find((t) => Math.abs(t.x - value) < 1e-9)?.text ?? '',
          margin: 16,
        },
        axisTick: { customValues: tickValues },
      },
      yAxis: {
        type: 'value',
        scale: true,
        name: 'Weekly revenue',
        nameLocation: 'middle',
        nameGap: 46,
        nameTextStyle: { fontSize: 11, color: '#5a5f66' },
        axisLabel: { formatter: (value: number) => `$${Math.round(value).toLocaleString()}` },
        splitLine: { lineStyle: { color: '#e5e7eb' } },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => this.formatTooltip(params, widget),
      },
      series: (sections.hasBefore
        ? [
            this.lineSeries('You', beforeRows, beforeX, 'merchantDollars', '#1f5fa8', 3, undefined, markArea),
            this.lineSeries('You', sinceRows, sinceX, 'merchantDollars', '#1f5fa8', 3),
            this.lineSeries(widget.peerGroup.name, beforeRows, beforeX, 'peerDollars', '#b85a1c', 2.5, [6, 5]),
            this.lineSeries(widget.peerGroup.name, sinceRows, sinceX, 'peerDollars', '#b85a1c', 2.5, [6, 5]),
          ]
        : [
            this.lineSeries('You', sinceRows, sinceX, 'merchantDollars', '#1f5fa8', 3, undefined, markArea),
            this.lineSeries(widget.peerGroup.name, sinceRows, sinceX, 'peerDollars', '#b85a1c', 2.5, [6, 5]),
          ]) as EChartsOption['series'],
    };
  }

  /**
   * Picks which rows get an axis tick (before-loyalty points are few, so all but the first/last get
   * one — those two sit right against the y-axis and the gap, where a label collides with its
   * neighbor; the exact value is still available on hover. After-loyalty is thinned to ~6), then
   * shows the year only the first time it appears among the visible labels, in position order (e.g.
   * Sep 8, 2025 ... Jul 6, 2026).
   */
  private buildAxisTicks(
    beforeRows: ChartRow[],
    sinceRows: ChartRow[],
    beforeX: number[],
    sinceX: number[]
  ): { x: number; text: string }[] {
    const beforeIndices = this.pickIndices(beforeRows.length, beforeRows.length).filter(
      (i) => beforeRows.length <= 2 || (i !== 0 && i !== beforeRows.length - 1)
    );
    const sinceIndices = this.pickIndices(sinceRows.length, 6);

    const entries = [
      ...beforeIndices.map((i) => ({ x: beforeX[i], category: beforeRows[i].category })),
      ...sinceIndices.map((i) => ({ x: sinceX[i], category: sinceRows[i].category })),
    ];

    let lastYear: number | null = null;
    return entries.map(({ x, category }) => {
      const date = moment(category);
      const year = date.year();
      const text = year !== lastYear ? `${date.format('MMM D')}\n${year}` : date.format('MMM D');
      lastYear = year;
      return { x, text };
    });
  }

  /** Evenly spaced indices into a range of `count`, capped at `maxLabels`, always including the last. */
  private pickIndices(count: number, maxLabels: number): number[] {
    if (count <= 0) {
      return [];
    }
    if (count <= maxLabels) {
      return Array.from({ length: count }, (_, i) => i);
    }
    const stride = Math.ceil(count / maxLabels);
    const indices: number[] = [];
    for (let i = 0; i < count; i += stride) {
      indices.push(i);
    }
    if (indices[indices.length - 1] !== count - 1) {
      indices.push(count - 1);
    }
    return indices;
  }

  private lineSeries(
    name: string,
    rows: ChartRow[],
    xs: number[],
    key: 'merchantDollars' | 'peerDollars',
    color: string,
    width: number,
    dashPattern?: number[],
    markArea?: unknown
  ): unknown {
    return {
      name,
      type: 'line',
      data: rows.map((row, i) => [xs[i], row[key], row.category]),
      connectNulls: false,
      showSymbol: false,
      lineStyle: dashPattern ? { color, width, type: dashPattern } : { color, width },
      itemStyle: { color },
      markArea,
    };
  }

  private buildMarkArea(widget: SalesComparisonWidget, sinceRows: ChartRow[], sinceX: number[]): any {
    const regions: unknown[] = [];
    if (widget.trialEndDate) {
      const trialIndices = sinceRows.map((_, i) => i).filter((i) => sinceRows[i].category <= widget.trialEndDate!);
      if (trialIndices.length > 0) {
        regions.push([
          {
            xAxis: sinceX[trialIndices[0]],
            itemStyle: { color: '#e8f1fb', borderColor: '#bfd7ef', borderWidth: 1, borderType: 'dashed' },
          },
          { xAxis: sinceX[trialIndices[trialIndices.length - 1]] },
        ]);
      }
    }
    return { silent: true, data: regions };
  }

  private formatTooltip(params: any, widget: SalesComparisonWidget): string {
    const withData = (Array.isArray(params) ? params : []).find((p: any) => p?.data?.[2] != null);
    const category = withData?.data?.[2];
    if (!category) {
      return '';
    }
    const row = this.rows.find((r) => r.category === category);
    const you = row?.merchantDollars ?? null;
    const peer = row?.peerDollars ?? null;
    const diff = you != null && peer != null ? you - peer : null;
    const fmt = (value: number | null) => (value == null ? '—' : `$${value.toLocaleString()}`);
    const diffLabel = diff == null ? '—' : `${diff > 0 ? '+' : ''}$${diff.toLocaleString()}`;
    return [
      moment(category).format('MMM D, YYYY'),
      `You: ${fmt(you)}`,
      `${widget.peerGroup.name}: ${fmt(peer)}`,
      `Difference: ${diffLabel}`,
    ].join('<br/>');
  }
}
